import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import { Button, message } from "antd";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import axios from "axios";
import { portalService } from "../../../../configs/constants";
import { useDispatch } from "react-redux";
import { setDriver, updateDriver } from "../duck/driverSlice";
import { downloadNewDriverPdf, downloadOldDriverPdf } from "../../../../utils/helper";

const DocumentErrorMessage = "Failed to download the document, document is not available.";

export default function RTATable(props: {
  total: number;

  callApi: (page: number) => void;
  loading: boolean;
}) {
  const { drivers, searchTerm } = useSelector((state: any) => state.driver);
  const { callApi, loading } = props;
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const isFirstLoad = React.useRef(true);
  const dispatch = useDispatch();
  const pageSize = 50;

  const filteredDrivers = useMemo(() => {
    if (!searchTerm) return drivers;
    return drivers.filter((driver: any) =>
      driver.fullName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [drivers, searchTerm]);

  // const handleApprove = async (driver: any) => {
  //   try {
  //     const payload = {
  //       documentRenewalId: driver._id,
  //       approved: true,
  //     };

  //     const response = await axios.post(
  //       `${portalService}/user/approve-driver-document-renewal`,
  //       payload
  //     );

  //     if (response?.data?.data) {
  //       const updatedDriver = response.data.data;

  //       message.success("Driver approved successfully.");

  //       const driverUpdated = drivers.map((item: any) =>
  //         item._id === updatedDriver._id ? { ...item, ...updatedDriver } : item
  //       );

  //       // ✅ Keep same shape as callApi
  //       dispatch(setDriver({ parsedData: driverUpdated, page }));
  //     }
  //   } catch (error) {
  //     message.error("Failed to approve driver.");
  //   }
  // };

  const handleApprove = async (driver: any) => {
    try {
      const payload = {
        documentRenewalId: driver._id,
        approved: true,
      };

      const response = await axios.post(
        `${portalService}/user/approve-driver-document-renewal`,
        payload
      );

      if (response?.data?.data) {
        message.success("Driver approved successfully.");

        // ✅ Fix: Use the correct ID comparison
        const driverUpdated = drivers.map((item: any) =>
          item._id === driver._id ? { ...item, approved: true } : item
        );

        // ✅ Keep same shape as callApi
        dispatch(updateDriver({ parsedData: driverUpdated }));
      }
    } catch (error) {
      message.error("Failed to approve driver.");
    }
  };

  const columns: GridColDef[] = [
    { field: "fusionId", headerName: "Fusion ID", flex: 1, minWidth: 120 },
    { field: "driverId", headerName: "Driver ID", flex: 1, minWidth: 120 },
    { field: "fullName", headerName: "Full Name", flex: 1, minWidth: 180 },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isApproved = params.row?.approved;

        return isApproved ? (
          <Button
            type="default"
            size="small"
            disabled
            style={{
              backgroundColor: "#d9d9d9",
              color: "#595959",
              cursor: "not-allowed",
              border: "none",
            }}
          >
            Approved
          </Button>
        ) : (
          <Button
            type="primary"
            size="small"
            onClick={() => handleApprove(params.row)}
          >
            Approve
          </Button>
        );
      },
    },
    {
      field: "document",
      headerName: "Old Document",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          type="primary"
          size="small"
          onClick={() => downloadOldDriverPdf(params.row).catch(() => message.error(DocumentErrorMessage))}
        >
          Preview
        </Button>
      ),
    },
    {
      field: "New",
      headerName: "New Document",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          type="primary"
          size="small"
          onClick={() => downloadNewDriverPdf(params.row).catch(() => message.error(DocumentErrorMessage))}
        >
          Preview
        </Button>
      ),
    },
    { field: "nationality", headerName: "Nationality", flex: 1, minWidth: 140 },
    {
      field: "rtaPermitNo",
      headerName: "RTA Permit No",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "createdAt",
      headerName: "Submitted at",
      flex: 1,
      minWidth: 180,

      // Used for sorting
      valueGetter: (params: any) => {
        const value = params.substr(0, 10);
        if (
          !value ||
          typeof value !== "string" ||
          value.trim() === "-" ||
          value.trim() === ""
        ) {
          return null;
        }
        const [year, month, day] = value.split("-");
        if (!day || !month || !year) return null;

        const isoDate = `${year}-${month}-${day}`; // "2024-03-22"
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date.toISOString(); // You can return `date` as well
      },

      // Used for display
      valueFormatter: (params: any) => {
        const value = params;
        if (!value) return "";

        const date = new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
      },
    },
    {
      field: "permitExpiryDate",
      headerName: "Permit Expiry Date",
      flex: 1,
      minWidth: 180,

      // Used for sorting
      valueGetter: (params: any) => {
        const value = params;
        if (
          !value ||
          typeof value !== "string" ||
          value.trim() === "-" ||
          value.trim() === ""
        ) {
          return null;
        }

        const [day, month, year] = value.split("/");
        if (!day || !month || !year) return null;

        const isoDate = `${year}-${month}-${day}`; // "2024-03-22"
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date.toISOString(); // You can return `date` as well
      },

      // Used for display
      valueFormatter: (params: any) => {
        const value = params;
        if (!value) return "";

        const date = new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
      },
    },
    {
      field: "trafficCode",
      headerName: "Traffic Code",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "dob",
      headerName: "Date of Birth",
      flex: 1,
      minWidth: 150,

      valueGetter: (params: any) => {
        const value = params;
        if (
          !value ||
          typeof value !== "string" ||
          value.trim() === "-" ||
          value.trim() === ""
        ) {
          return null;
        }

        const [day, month, year] = value.split("/");
        if (!day || !month || !year) return null;

        const isoDate = `${year}-${month}-${day}`;
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date.toISOString();
      },

      valueFormatter: (params: any) => {
        const value = params;
        if (!value) return "";

        const date = new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB");
      },
    },
    { field: "mobileNo", headerName: "Mob No", flex: 1, minWidth: 140 },
    { field: "eidNo", headerName: "EID No", flex: 1, minWidth: 150 },
    {
      field: "eidExpiryDate",
      headerName: "EID Expiry Date",
      flex: 1,
      minWidth: 180,

      // Used for sorting
      valueGetter: (params: any) => {
        const value = params;
        if (
          !value ||
          typeof value !== "string" ||
          value.trim() === "-" ||
          value.trim() === ""
        ) {
          return null;
        }

        const [day, month, year] = value.split("/");
        if (!day || !month || !year) return null;

        const isoDate = `${year}-${month}-${day}`; // Format as "YYYY-MM-DD"
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date.toISOString(); // Return ISO string for consistent sorting
      },

      // Used for display
      valueFormatter: (params: any) => {
        const value = params;
        if (!value) return "";

        const date = new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB"); // Format as "DD/MM/YYYY"
      },
    },
    {
      field: "drivingLicenseNo",
      headerName: "Driving License No",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "dlExpiryDate",
      headerName: "DL Expiry Date",
      flex: 1,
      minWidth: 180,

      // Enable proper sorting
      valueGetter: (params: any) => {
        const value = params;
        if (
          !value ||
          typeof value !== "string" ||
          value.trim() === "-" ||
          value.trim() === ""
        ) {
          return null;
        }

        const [day, month, year] = value.split("/");
        if (!day || !month || !year) return null;

        const isoDate = `${year}-${month}-${day}`;
        const date = new Date(isoDate);
        return isNaN(date.getTime()) ? null : date.toISOString(); // ISO format ensures correct sorting
      },

      valueFormatter: (params: any) => {
        const value = params;
        if (!value) return "";

        const date = new Date(value);
        return isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-GB"); // Display as "DD/MM/YYYY"
      },
    },
  ];

  return (
    <>
      <div
        style={{
          height: "80vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DataGrid
          rows={filteredDrivers}
          columns={columns}
          getRowId={(r) => r._id}
          disableRowSelectionOnClick
          hideFooter
          slots={{
            columnSortedAscendingIcon: () => null,
            columnSortedDescendingIcon: () => null,
          }}
        />
        <div ref={sentinelRef} style={{ height: 1 }} />
        <div
          style={{
            textAlign: "center",
            padding: 8,
            fontSize: 12,
            opacity: 0.7,
          }}
        ></div>
      </div>
    </>
  );
}
